import React from 'react';
import { Badge } from '../../BasicComponents/Badge';
import type { ExpiryAlertBadgeProps } from './types';

// ─── ExpiryAlertBadge ─────────────────────────────────────────────────────────

export function ExpiryAlertBadge({
  expiryDate,
  daysRemaining,
  thresholds = { warning: 30, critical: 7 },
  className,
}: ExpiryAlertBadgeProps) {
  // Prefer the server's day count (computed against the business day boundary)
  // so the badge, the API severity, and the exported CSV always agree. Only fall
  // back to a local-timezone derivation when the caller has no server value —
  // that fallback can be off by one near a boundary (GitHub #233).
  let daysLeft: number;
  if (daysRemaining !== undefined) {
    daysLeft = daysRemaining;
  } else {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    daysLeft = Math.floor((expiry.getTime() - today.getTime()) / 86_400_000);
  }

  // Safe — do not render
  if (daysLeft > thresholds.warning) return null;

  if (daysLeft < 0) {
    return (
      <span
        role="status"
        aria-label="Product is expired"
        className={className}
      >
        <Badge variant="expiryPast" size="sm">EXPIRED</Badge>
      </span>
    );
  }

  if (daysLeft <= thresholds.critical) {
    return (
      <span
        role="status"
        aria-label={`Product expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
        className={className}
      >
        <Badge variant="expiry7d" size="sm">Expires in {daysLeft}d</Badge>
      </span>
    );
  }

  // warning range: critical < daysLeft <= warning
  return (
    <span
      role="status"
      aria-label={`Product expires in ${daysLeft} days`}
      className={className}
    >
      <Badge variant={daysLeft <= 15 ? 'expiry15d' : 'expiry30d'} size="sm">
        Expires in {daysLeft}d
      </Badge>
    </span>
  );
}
